import { Injectable } from '@angular/core';
import {
  AccountingYear,
  Sub_EncouragementPoints,
  PointTransfer,
  ProtocolEncouragementPoints,
  ProtocolWorkHours,
  Users,
  Sub_WorkHours,
  Properties,
  TransferState,
  Event,
  Sub_WorkPackage,
  Sub_Participant,
  MethodReturn,
  IParticipant,
  WorkPackageKind,
  Groups,
  Sub_UserRights,
  User,
  Group,
  Calendars,
  Calendar,
  CalendarEvent,
  CalendarEventParticipant,
  CalendarContainer,
  Right,
  InboxNotification,
  Sub_Notifications,
} from './definitions.service';
import firebase from 'firebase/app';
import 'firebase/firestore';
import { LogService } from './log.service';
import { DatabaseService } from './database-service.service';
import { combineLatest, Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
// import { Firestore, collection, getDocs, doc, getDoc, CollectionReference } from "firebase/firestore";

export enum WorkHourEntryAction {
  UNDEFINED = 0,
  ADD = 1,
  REMOVE = 2,
}

export enum WorkHourEntryReason {
  UNDEFINED = 0,
  STANDARD = 1,
  DELETED_ALL_FOR_YEAR_TRANSFER = 10, //on every change of a year, all work hours will be cleared
  EVENT_POST_PROCESSING_WH_CREDIT = 11,
  FIX_SUM_WORK_HOURS = 12,
}

export enum EncouragementPointEntryAction {
  UNDEFINED = 0,
  ADD = 1,
  REMOVE = 2,
}

export enum EncouragementPointReason {
  UNDEFINED = 0,
  STANDARD = 1,
  POINT_TRANSFER = 2,
  TRANSFER_FROM_WORK_HOUR_BY_YEAR_END = 11,
  FIX_SUM_ENC_POINTS = 12,
}

@Injectable({
  providedIn: 'root',
})
export class DataModelService {
  public list: any[];

  constructor(
    private log: LogService,
    private db: DatabaseService
  ) { }

  /**
   * Erstellt einen neuen Arbeitsstunden-Eintrag bei einem user.
   * Dabei wird auch ein Protokoll-Eintrag geschrieben.
   *
   * @returns true if successful, false if not
   */
  public async createWorkHourEntry(
    destinationDocumentId: string,
    amount: number,
    action: WorkHourEntryAction,
    comment: string = '',
    reason: WorkHourEntryReason = WorkHourEntryReason.STANDARD,
    actionDoneBy: string,
    entryCreatorName: string
  ) {
    if (!destinationDocumentId) {
      this.log.error('Keine user document id angegeben', DataModelService.name);
      return false;
    }

    try {
      var data = await this.loadCurrentAccountingYear();
      var accountingYearDocId = data.id;
    } catch (error) {
      this.log.error(
        'Konnte Daten des Abrechnungsjahr nicht laden',
        error,
        DataModelService.name
      );
      return false;
    }

    //create work hour entry
    var whId = '';
    var creationTimestamp = firebase.firestore.Timestamp.fromDate(new Date());
    try {
      await this.createWorkHourEntryHelper(
        destinationDocumentId,
        +amount,
        action,
        comment,
        reason,
        accountingYearDocId,
        creationTimestamp,
        actionDoneBy,
        entryCreatorName
      );
    } catch (error) {
      this.log.error(
        'Konnte WorkHour-entry nicht anlegen',
        error,
        DataModelService.name
      );
      return false;
    }

    //get newly created workHour entry id
    try {
      var docId = await this.getUserSubcollectionDocByTimestamp(
        destinationDocumentId,
        creationTimestamp,
        Sub_WorkHours()
      );
      whId = docId;
    } catch (error) {
      this.log.error(
        'Konnte WorkHour-entry nicht anlegen',
        error,
        DataModelService.name
      );
      return false;
    }

    if (whId == undefined) {
      this.log.error(
        'WorkHour-entry document id ist undefiniert',
        DataModelService.name
      );
      return false;
    }

    var logData = {
      destinationDocumentId: destinationDocumentId,
      amount: amount,
      action: action,
      comment: comment,
      reason: reason,
      actionDoneBy: actionDoneBy,
      entryCreatorName: entryCreatorName,
      idAccountingYearSetting: accountingYearDocId,
      idWorkHour: whId,
    };

    //create log entry
    this.db.createLogEntry(logData, actionDoneBy, "createWorkHourEntry")
    return true;
  }

  /**
   * Creates a new entry in the sub-collection 'workHour'.
   *
   * @returns a promise for this transaction
   */
  private async createWorkHourEntryHelper(
    userDocumentid: string,
    amount: number,
    action: WorkHourEntryAction,
    comment: string = '',
    reason: WorkHourEntryReason = WorkHourEntryReason.STANDARD,
    accountingYearSettingId: string,
    creationTimestamp: firebase.firestore.Timestamp,
    actionDoneBy: string,
    entryCreatorName: string
  ) {
    var data = {
      accountingYearSetting: accountingYearSettingId,
      action: action,
      actionComment: comment,
      actionDoneBy: actionDoneBy,
      actionDoneByName: entryCreatorName,
      amount: amount,
      reason: reason,
      timestamp: creationTimestamp,
    };

    var workHoursCollRef = this.db.getFirestore
      .collection(Users())
      .doc(userDocumentid)
      .collection(Sub_WorkHours())
      .doc();
    var userRef = this.db.getFirestore
      .collection(Users())
      .doc(userDocumentid);

    return this.db.getFirestore.runTransaction((transaction) => {
      return transaction.get(userRef).then((res) => {
        if (!res.exists) {
          throw 'User does not exist!';
        }

        // berechne Gesamtzahl
        var sumWorkHours = +res.data().sumWorkHours;
        var newSum = 0;
        if (sumWorkHours) {
          if (data.action == WorkHourEntryAction.ADD) {
            newSum = sumWorkHours + data.amount;
          } else {
            newSum = sumWorkHours - data.amount;
          }
        } else {
          newSum = data.amount;
        }

        // Commit to Firestore
        transaction.update(userRef, {
          sumWorkHours: newSum,
        });
        transaction.set(workHoursCollRef, data);
      });
    });
  }


  /**
   * Erstellt einen neuen Bonuspunkt-eintrag bei einem user.
   * dabei wird auch ein protokoll-Eintrag geschrieben.
   * 
   * @param userDocumentid - the user id (which is the document id as well)
   * @param amount - the amount of points
   * @param action - either add or substract the points
   * @param comment - a comment for th action
   * @param reason - a specific reason (see: EncouragementPointReason)
   * @param actionDoneById - the id of the user that initiated the creation of this points 
   * @param unlockTimestamp - a timestamp that defines when this points are unlocked
   * @param tags - additional tags for this entry
   * @param actionDoneByName - the pretty printed name of the user that created the entry 
   * @param receiverUserId - User id that receives points (only applicable if points are substracted (e.g. point transfer))
   * @param receiverUserName - User name that receives points (only applicable if points are substracted (e.g. point transfer))
   * @returns 
   */
  public async createEncouragementPointEntry(
    userDocumentid: string,
    amount: number,
    action: EncouragementPointEntryAction,
    comment: string = '',
    reason: EncouragementPointReason = EncouragementPointReason.STANDARD,
    actionDoneById: string,
    unlockTimestamp: firebase.firestore.Timestamp,
    tags: string[],
    actionDoneByName: string,
    receiverUserId: string = "<nicht vorhanden>",
    receiverUserName: string = "<nicht vorhanden>"
  ) {
    if (!userDocumentid) {
      this.log.error('Keine user document id angegeben', DataModelService.name);
      return false;
    }

    //create work hour entry
    var epId = '';
    var creationTimestamp = firebase.firestore.Timestamp.fromDate(new Date());
    try {
      await this.createEncouragementPointEntryHelper(
        userDocumentid,
        +amount,
        action,
        comment,
        reason,
        actionDoneById,
        unlockTimestamp,
        tags,
        actionDoneByName,
        receiverUserId,
        receiverUserName
      );
    } catch (error) {
      console.error(error);
      this.log.error('Konnte Förderpunkt-Eintrag nicht anlegen', error);
      return false;
    }

    //get newly created encouragement points entry id
    try {
      var docId = await this.getUserSubcollectionDocByTimestamp(
        userDocumentid,
        creationTimestamp,
        Sub_EncouragementPoints()
      );
      epId = docId;
    } catch (error) {
      this.log.error(
        'Konnte Förderpunkt-Eintrag nicht anlegen',
        error,
        DataModelService.name
      );
      return false;
    }

    if (epId == undefined) {
      this.log.error(
        'Förderpunkt-Eintrag id nicht angegeben',
        DataModelService.name
      );
      return false;
    }

    var logData = {
      userDocumentid: userDocumentid,
      amount: amount,
      action: action,
      comment: comment,
      reason: reason,
      actionDoneById: actionDoneById,
      unlockTimestamp: unlockTimestamp,
      tags: tags,
      actionDoneByName: actionDoneByName,
      receiverUserId: receiverUserId,
      receiverUserName: receiverUserName,
      idEncouragementPoint: epId
    };

    //create log entry
    this.db.createLogEntry(logData, actionDoneById, "createEncouragementPointEntry")
    return true;
  }


  /**
   * Erstellt einen neuen Bonuspunkt-eintrag bei einem user.
   * dabei wird auch ein protokoll-Eintrag geschrieben.
   * 
   * @param userDocumentid - the user id (which is the document id as well)
   * @param amount - the amount of points
   * @param action - either add or substract the points
   * @param comment - a comment for th action
   * @param reason - a specific reason (see: EncouragementPointReason)
   * @param actionDoneById - the id of the user that initiated the creation of this points 
   * @param unlockTimestamp - a timestamp that defines when this points are unlocked
   * @param tags - additional tags for this entry
   * @param actionDoneByName - the pretty printed name of the user that created the entry 
   * @param receiverUserId - User id that receives points (only applicable if points are substracted (e.g. point transfer))
   * @param receiverUserName - User name that receives points (only applicable if points are substracted (e.g. point transfer))
   * @returns 
   */
  private async createEncouragementPointEntryHelper(
    userDocumentid: string,
    amount: number,
    action: EncouragementPointEntryAction,
    comment: string = '',
    reason: EncouragementPointReason = EncouragementPointReason.STANDARD,
    actionDoneById: string,
    unlockTimestamp: firebase.firestore.Timestamp,
    tags: string[],
    actionDoneByName: string,
    receiverUserId: string = "<nicht vorhanden>",
    receiverUserName: string = "<nicht vorhanden>"
  ) {
    var creationTimestamp = firebase.firestore.Timestamp.fromDate(new Date());

    //Note: receiver only applicable when points are reduced (e.g. point transfer)
    var data = {
      action: action,
      actionComment: comment,
      actionDoneBy: actionDoneById,
      actionDoneByName: actionDoneByName,
      amount: amount,
      reason: reason,
      timestamp: creationTimestamp,
      unlockedAt: unlockTimestamp,
      tags: tags,
      receiverUserId: receiverUserId,
      receiverUserName: receiverUserName
    };

    var encouragementPointsCollRef = this.db.getFirestore
      .collection(Users())
      .doc(userDocumentid)
      .collection(Sub_EncouragementPoints())
      .doc();
    var userRef = this.db.getFirestore
      .collection(Users())
      .doc(userDocumentid);

    return this.db.getFirestore.runTransaction((transaction) => {
      return transaction.get(userRef).then((res) => {
        if (!res.exists) {
          throw 'User does not exist!';
        }

        // berechne Gesamtzahl
        var sumEncouragementPoints = +res.data().sumEncouragementPoints;
        var newSum = 0;
        if (sumEncouragementPoints) {
          if (data.action == EncouragementPointEntryAction.ADD) {
            newSum = sumEncouragementPoints + data.amount;
          } else {
            newSum = sumEncouragementPoints - data.amount;
          }
        } else {
          newSum = data.amount;
        }

        // Commit to Firestore
        transaction.update(userRef, {
          sumEncouragementPoints: newSum,
        });
        transaction.set(encouragementPointsCollRef, data);
      });
    });
  }

  /**
   * Updates the state of a point transfer entry.
   *
   * @returns a promise for this operation.
   */
  public updateTransferEntryState(id: string, state: TransferState) {
    return new Promise<boolean>((resolve, reject) => {
      var ref = this.db.getAngularFirestore.collection(PointTransfer()).doc(id).ref;
      var data = {
        state: state,
      };
      ref
        .update(data)
        .then(() => {
          this.log.debug(
            'PointTransfer-document successfully updated on db',
            id,
            data,
            DataModelService.name
          );
          resolve(true);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Updates the data of a user.
   *
   * @returns a promise for this operation.
   */
  public updateUser(uid: string, data: any, log: boolean): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      var ref = this.db.getAngularFirestore.collection(Users()).doc(uid).ref;
      ref
        .update(data)
        .then(() => {
          this.log.debug(
            'User-document successfully saved on db',
            uid,
            data,
            DataModelService.name
          );

          if (log) {
            var logData = {
              data: data,
              uid: uid,
            };

            //create log entry
            this.db.createLogEntry(logData, "Unknown", "updateUser");
          }
          resolve(true);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Gets a subcollection document of a user.
   *
   * @returns a promise for this operation.
   */
  public getUserSubcollectionDocByTimestamp(
    uid: string,
    timestamp: firebase.firestore.Timestamp,
    subCollectionName: string
  ): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      var ref = this.db.getAngularFirestore
        .collection(Users())
        .doc(uid)
        .collection(subCollectionName).ref;

      // Create a query against the collection.
      var query = ref.where('timestamp', '==', timestamp);

      return query
        .get()
        .then((querySnapshot) => {
          querySnapshot.forEach((doc) => {
            resolve(doc.id);
          });
        })
        .catch((error) => {
          var msg =
            'Error getting ' + subCollectionName + ' work hour data: ' + error;
          reject(msg);
        });
    });
  }

  // ------------To be checked -----------------------------

  //Delete all docs of a sub collection
  private async deleteSubCollectionData(path: string): Promise<MethodReturn> {
    var ret: MethodReturn = { result: false };
    var loaded = await this.db.get(path);
    if (loaded.result) {
      var subs = loaded.data;

      for (let index = 0; index < subs.length; index++) {
        const element = subs[index];
        var p = element[Properties.Path];
        var deleted = await this.db.delete(p, false);
        if (!deleted.result) {
          return deleted;
        }
      }
      ret.result = true;
    }
    return ret;
  }

  // ----------------------------------------------------- Done


  /**
   * creates a new entry in the point transfer collection
   *
   * @returns true if successful, false if not.
   */
  public async createPointTransferEntry(
    senderUserId: string,
    senderUserName: string,
    receiverUserId: string,
    receiverUserName: string,
    amountPoints: number,
    comment: string,
    state: string
  ) {
    if (
      !senderUserId ||
      !senderUserName ||
      !receiverUserId ||
      !receiverUserName ||
      !amountPoints ||
      !comment ||
      !state
    ) {
      this.log.error('Ungültige Daten angegeben', DataModelService.name);
      return false;
    }
    var ts = firebase.firestore.Timestamp.fromDate(new Date());
    var data = {
      senderUserId: senderUserId,
      senderUserName: senderUserName,
      receiverUserId: receiverUserId,
      receiverUserName: receiverUserName,
      amountPoints: +amountPoints,
      comment: comment,
      state: state,
      timestamp: ts,
    };
    var res = await this.db.add(data, PointTransfer());
    if (!res.result) {
      this.log.error(
        'Konnte Punkte-Transfer daten nicht speichern.',
        DataModelService.name
      );
      return false;
    } else {
      return res.id;
    }
  }

  /**
   * Loads the data of a collection
   *
   * @returns a promise for this operation.
   */
  public loadAllCollectionsData(
    collectionName: string,
    constraintKey: string = null,
    constraintValue: string = null
  ): Promise<any> {
    return new Promise<any>(async (resolve, reject) => {
      var ret = await this.db.get(
        collectionName,
        true,
        constraintKey,
        constraintValue
      );
      if (ret.result) {
        resolve(ret.data);
      } else {
        reject(ret.errorMessage);
      }
    });
  }

  /**
   * Loads a collection item by a given id.
   *
   * @returns a promise for this operation
   */
  public async getCollectionItemById(
    collectionName: string,
    id: string
  ): Promise<any> {
    return new Promise<any>(async (resolve, reject) => {
      var path = `${collectionName}/${id}`;
      var ret = await this.db.get(path);
      if (ret.result) {
        resolve(ret.data);
      } else {
        reject(ret.errorMessage);
      }
    });
  }

  public async getByPath(path: string): Promise<MethodReturn> {
    return await this.db.getDocDirect(path);
  }

  /**
   * Loads a user by its email.
   *
   * @returns a promise for this operation.
   */
  public async loadUserByEmail(email: string): Promise<any> {
    return new Promise<any>(async (resolve, reject) => {
      var data = await this.db.get(Users(), true, Properties.Email, email);
      if (data.result) {
        if (data?.data?.length == 0) {
          reject('Benutzer nicht vorhanden');
        } else {
          if (data?.data.length > 1) {
            reject('Mehrere Nutzer mit dieser E-Mail registriert');
          }
          var ret = data?.data[0];
          resolve(ret);
        }
      } else {
        reject(undefined);
      }
    });
  }

  /**
   * Checks if an entry is already existing
   *
   * @param collectionPath
   * @param userId
   * @returns the object if existing, undefined if not.
   */
  public async entryExisting(
    collectionPath: string,
    userId: string
  ): Promise<any> {
    return new Promise<any>(async (resolve, reject) => {
      var data = await this.db.get(
        collectionPath,
        true,
        Properties.UserId,
        userId
      );
      if (data.result) {
        var ret = data?.data[0];
        resolve(ret);
      } else {
        reject(undefined);
      }
    });
  }

  /**
   * Gets any specified direct subcollection of a user.
   *
   * @returns a promise for this operation.
   */
  public async getUserSubcollectionDoc(
    uid: string,
    subCollectionName: string
  ): Promise<any> {
    return new Promise<any>(async (resolve, reject) => {
      var path = `${Users()}/${uid}/${subCollectionName}`;
      var ret = await this.db.get(path);
      if (ret.result) {
        resolve(ret.data);
      } else {
        reject(ret.errorMessage);
      }
    });
  }

  /**
   * Loads all users data.
   *
   * @returns a promise for this operation.
   */
  public async loadAllUsers(
    delaySpinner: boolean = false,
    hideSpinner: boolean = false
  ): Promise<any> {
    return new Promise<any>(async (resolve, reject) => {
      var ret = await this.db.get(
        Users(),
        false,
        '',
        '',
        delaySpinner,
        hideSpinner
      );
      if (ret.result) {
        resolve(ret.data);
      } else {
        reject(ret.errorMessage);
      }
    });
  }

  /**
   * Loads the data of a user
   *
   * @returns a promise for this operation.
   */
  public async loadUser(
    uid: string,
    forceFromProduction: boolean = false,
    delaySpinner: boolean = false,
    hideSpinner: boolean = false,
    delayTime: number = 1000
  ): Promise<any> {
    var collection = Users();
    if (forceFromProduction) {
      collection = 'users';
    }
    return new Promise<any>(async (resolve, reject) => {
      var ret = await this.db.getDoc(
        collection,
        uid,
        delaySpinner,
        hideSpinner,
        delayTime
      );
      if (ret.result) {
        resolve(ret.data);
      } else {
        reject(ret.errorMessage);
      }
    });
  }

  /**
   * Loads the currently active accounting year.
   *
   * @returns a promise for this operation.
   */
  public loadCurrentAccountingYear(): Promise<any> {
    return new Promise<any>((resolve, reject) => {

      // Create a reference to the cities collection
      var ref = this.db.getAngularFirestore.collection(AccountingYear()).ref;
      var currentTime = firebase.firestore.Timestamp.fromDate(new Date());
      // Create a query against the collection.
      var query = ref.where('end', '>=', currentTime);

      query
        .get()
        .then((querySnapshot) => {
          querySnapshot.forEach((doc) => {
            var accountingYearData: any = doc.data();
            var endTime: firebase.firestore.Timestamp = accountingYearData.end;
            var showWarning: boolean = false;

            //check if currentTime is below end time
            if (currentTime > endTime) {
              showWarning = true;
            }

            var data = {
              data: accountingYearData,
              id: doc.id,
              showWarning: showWarning,
            };
            resolve(data);
          });
          reject('Kein Eintrag gefunden der den aktuellen Tag beinhaltet');
        })
        .catch((error) => {
          this.log.error(
            'Error getting current AccountingYearSetting',
            error,
            DataModelService.name
          );
          reject(error);
        });
    });
  }

  /**
   * Deletes a work hour entry from a user.
   *
   * @returns a promise for this operation.
   */
  public async deleteWorkHourEntryDocument(
    userDocumentId: string,
    docRef: string,
    userFirstName: string,
    userLastName: string,
    elementData: any
  ): Promise<MethodReturn> {
    var one = `${Users()}/${userDocumentId}/${Sub_WorkHours()}`;
    return this.deleteDocAndWriteProtEntry(
      one,
      docRef,
      ProtocolWorkHours(),
      userDocumentId,
      userFirstName,
      userLastName,
      elementData
    );
  }

  /**
   * Deletes a encouragement point entry from a user
   *
   * @returns a promise for this operation.
   */
  public deleteEncouragementPointEntryDocument(
    userDocumentId: string,
    docRef: string,
    userFirstName: string,
    userLastName: string,
    elementData: any
  ): Promise<any> {
    var path = `${Users()}/${userDocumentId}/${Sub_EncouragementPoints()}`;
    return this.deleteDocAndWriteProtEntry(
      path,
      docRef,
      ProtocolEncouragementPoints(),
      userDocumentId,
      userFirstName,
      userLastName,
      elementData
    );
  }

  /**
   * Deletes the document and writes an according protocol entry.
   *
   * @param collectionPath The path to the collection (e.g.: users/<id>/workHours)
   * @param entryId The id of the document
   * @param protocolCollectionName The name of the according protocol collection
   * @param userId The id of the user who has deleted the entry
   * @returns A promise of a method return.
   */
  public async deleteDocAndWriteProtEntry(
    collectionPath: string,
    entryId: string,
    protocolCollectionName: string,
    userId: string,
    userFirstName: string,
    userLastName: string,
    elementData: any
  ): Promise<MethodReturn> {
    //----------- find entry
    var command = await this.db.getDoc(collectionPath, entryId);
    if (!command.result) return command;

    //----------- delete entry
    var path = `${collectionPath}/${entryId}`;
    command = await this.db.delete(path, false);
    if (!command.result) {
      return command;
    }

    //---------- create protocol log entry
    var logData = {
      collectionPath: collectionPath,
      entryId: entryId,
      protocolCollectionName: protocolCollectionName,
      userId: userId,
      userFirstName: userFirstName,
      userLastName: userLastName,
      elementData: elementData
    };

    //create log entry
    this.db.createLogEntry(logData, "Unknown", "deleteDocAndWriteProtEntry");
    return command;
  }

  /**
   * Creates a new event.
   *
   * @returns The id of the event or undefined if rejected or error occurred.
   */
  public async setEvent(
    idAccountingYear: string,
    name: string,
    description: string,
    locationName: string,
    houseNumber: string,
    street: string,
    city: string,
    plz: number,
    responsiblePerson: string = '',
    responsiblePersonUid: string = '',
    start: Date,
    end: Date,
    eventId: string = '',
    update: boolean = false
  ): Promise<MethodReturn> {
    var ret: MethodReturn = { result: false };

    var data = {
      idAccountingYear: idAccountingYear,
      name: name,
      locationName: locationName,
      description: description,
      houseNumber: houseNumber,
      street: street,
      plz: plz,
      city: city,
      responsiblePerson: responsiblePerson,
      responsiblePersonId: responsiblePersonUid,
      start: firebase.firestore.Timestamp.fromDate(start),
      end: firebase.firestore.Timestamp.fromDate(end),
      releasedForUsers: false,
      processedInBackend: false,
      numericCode: 0,
    };

    //check date ranges
    if (start > end) {
      var msg = 'Das Start-Datum muss vor dem End-Datum liegen';
      this.log.error(msg, DataModelService.name);
      ret.errorMessage = msg;
      return ret;
    }
    var set: MethodReturn;
    if (update) {
      var set = await this.db.update(data, Event(), eventId);
    } else {
      var set = await this.db.add(data, Event());

      //set numeric code
      if (set.result) {
        var codeSet = await this.setEventNumericCode(set.id);
        if (codeSet.result) {
          return set;
        } else {
          ret.errorMessage = 'Konnte event code nicht setzen';
          return ret;
        }
      }
    }
    return set;
  }

  public async setEventProcessedInBackend(
    eventId: string,
    processed: boolean
  ): Promise<MethodReturn> {
    var data = {
      processedInBackend: processed,
    };
    return await this.db.update(data, Event(), eventId);
  }

  public async setEventReleasedForUsers(
    eventId: string,
    released: boolean
  ): Promise<MethodReturn> {
    var data = {
      releasedForUsers: released,
    };
    return await this.db.update(data, Event(), eventId);
  }

  public async setWpReleasedForUsers(
    docPath: string,
    released: boolean
  ): Promise<MethodReturn> {
    var data = {
      releasedForUsers: released,
    };
    return await this.db.updateDirect(data, docPath);
  }

  //Sets the presence state of a participant
  public async setPresenceForEventParticipant(
    docId: string,
    present: boolean
  ): Promise<MethodReturn> {
    var data = {
      present: present,
    };
    return await this.db.updateDirect(data, docId);
  }

  //Sets the numeric code of an event
  private async setEventNumericCode(eventId: string): Promise<MethodReturn> {
    var hash = Math.abs(+this.generateHashCode(eventId));
    var data = {
      numericCode: hash,
    };
    return await this.db.update(data, Event(), eventId);
  }

  //Updates an event with the given data
  public async updateEvent(eventId: string, data: any): Promise<MethodReturn> {
    return await this.db.update(data, Event(), eventId);
  }

  /**
   * Adds a new work package to an existing event.
   *
   * @returns the id of the workPackage or undefined if rejected or error occurred.
   */
  public async setWorkPackage(
    collectionPath: string,
    type: string,
    minParticipants: number,
    start: Date,
    end: Date,
    responsiblePersonName: string = '',
    responsiblePersonUid: string = '',
    wpId: string = '',
    kind: WorkPackageKind = WorkPackageKind.Standard,
    update: boolean = false,
    releasedForUsers: boolean = false,
    isUe18: boolean = true,
    skipShiftCollision: boolean = false
  ): Promise<MethodReturn> {
    var collPath = `${collectionPath}/${Sub_WorkPackage()}`;
    if (update) {
      collPath = collectionPath;
    }

    var data = {
      type: type,
      minParticipants: minParticipants,
      responsiblePerson: responsiblePersonName,
      responsiblePersonUid: responsiblePersonUid,
      start: firebase.firestore.Timestamp.fromDate(start),
      end: firebase.firestore.Timestamp.fromDate(end),
      kind: kind,
      releasedForUsers: releasedForUsers,
      isUe18: isUe18,
      skipShiftCollision: skipShiftCollision,
    };

    var set: MethodReturn;
    if (update) {
      var set = await this.db.update(data, collPath, wpId);
    } else {
      var set = await this.db.add(data, collPath);
    }
    return set;
  }

  //Marks a work packages as started or stopped. This is used for special work packages like 'Aufbau' and 'Abbau'.
  //Sets the defined start/end time to current time.
  public async markWorkPackageStarted(
    collectionPath: string,
    start: boolean
  ): Promise<MethodReturn> {
    var data = {};
    var now = new Date();

    if (start) {
      // //set end 4 hours later
      // var later = new Date(now);
      // later.setHours(now.getHours() + 4);
      data = {
        definedStart: firebase.firestore.Timestamp.fromDate(now),
        // end: firebase.firestore.Timestamp.fromDate(later),
        running: true,
      };
    } else {
      data = {
        definedEnd: firebase.firestore.Timestamp.fromDate(now),
        running: false,
      };
    }
    return await this.db.updateDirect(data, collectionPath);
  }

  //Add a single participant
  public async addParticipant(
    collectionPathWorkPackage: string,
    data: IParticipant
  ): Promise<MethodReturn> {
    data[Properties.RegistrationTime] = firebase.firestore.Timestamp.fromDate(new Date());
    var path = `${collectionPathWorkPackage}/${Sub_Participant()}`;
    return await this.db.add(data, path);
  }

  /**
   * Adds the whole list of participants to a work package
   *
   * @param collectionPath path to the according work package
   * @param data participant data list to add
   * @returns a method return object
   */
  public async setParticipants(
    collectionPath: string,
    data: any[]
  ): Promise<MethodReturn> {
    // delete all participants
    var deleted = await this.deleteParticipants(collectionPath);
    if (!deleted.result) {
      return deleted;
    }
    data.forEach(element => {
      element[Properties.RegistrationTime] = firebase.firestore.Timestamp.fromDate(new Date());
    });
    var path = `${collectionPath}/${Sub_Participant()}`;
    return await this.db.batchAdd(data, path);
  }

  //Update a participant
  public async updateParticipant(data: IParticipant): Promise<MethodReturn> {
    return await this.db.update(data, data.parent, data.id);
  }

  //Delete all participants of an work package
  public async deleteParticipants(pathWP: string): Promise<MethodReturn> {
    var path = `${pathWP}/${Sub_Participant()}`;
    return this.deleteSubCollectionData(path);
  }

  //Deletes all work packages of an event
  public async deleteWorkPackages(pathEvent: string): Promise<MethodReturn> {
    var path = `${pathEvent}/${Sub_WorkPackage()}`;
    return this.deleteSubCollectionData(path);
  }

  public async deleteGeneric(path: string, log: boolean): Promise<MethodReturn> {
    var ret = await this.db.delete(path, log);
    return ret;
  }

  public async getEventsWithSubData(
    delaySpinner: boolean = false,
    hideSpinner: boolean = false,
    useDocumentUidAsId: boolean = true,
    clearSpinnerAtEnd: boolean = true,
    queryFor: boolean = false,
    property: string = '',
    equalityValue: any = '',
    queryOperator: firebase.firestore.WhereFilterOp = "==",
    onlyReleased: boolean = true
  ): Promise<MethodReturn> {
    return await this.db.getEventsWithSubData(delaySpinner, hideSpinner, useDocumentUidAsId, clearSpinnerAtEnd, queryFor, property, equalityValue, queryOperator, onlyReleased);
  }


  public async get(
    collectionName: string,
    delaySpinner: boolean = false,
    hideSpinner: boolean = false,
    useDocumentUidAsId: boolean = true,
    clearSpinnerAtEnd: boolean = true
  ): Promise<MethodReturn> {
    return await this.db.get(
      collectionName,
      false,
      '',
      '',
      delaySpinner,
      hideSpinner, useDocumentUidAsId,
      clearSpinnerAtEnd
    );
  }

  public async getDoc(
    collectionName: string,
    docId: string,
    delaySpinner: boolean = false,
    hideSpinner: boolean = false,
    delayTime: number = 1000
    , clearSpinnerAtEnd: boolean = true
  ): Promise<MethodReturn> {
    return await this.db.getDoc(
      collectionName,
      docId,
      delaySpinner,
      hideSpinner,
      delayTime, clearSpinnerAtEnd
    );
  }

  /**
   * Add notification token.
   *
   * @returns a promise for this operation.
   */
  public async addNotificationToken(
    userDocumentid: string,
    token: string
  ): Promise<MethodReturn> {
    var data = {
      notificationIds: firebase.firestore.FieldValue.arrayUnion(token),
    };
    return await this.db.update(data, Users(), userDocumentid);
  }

  public async saveInboxNotification(
    userId: string,
    notification: InboxNotification
  ): Promise<MethodReturn> {
    const path = Users() + '/' + userId + '/' + Sub_Notifications();
    return await this.db.add(notification, path);
  }

  public subscribeToInboxNotifications(userId: string): Observable<InboxNotification[]> {
    const path = Users() + '/' + userId + '/' + Sub_Notifications();
    return this.db.getAngularFirestore
      .collection<InboxNotification>(path, ref => ref.orderBy('timestamp', 'desc'))
      .valueChanges({ idField: 'id' });
  }

  public async markNotificationRead(userId: string, notificationId: string): Promise<void> {
    const docPath = Users() + '/' + userId + '/' + Sub_Notifications() + '/' + notificationId;
    await this.db.updateDirect({ read: true }, docPath);
  }

  public async markNotificationUnread(userId: string, notificationId: string): Promise<void> {
    const docPath = Users() + '/' + userId + '/' + Sub_Notifications() + '/' + notificationId;
    await this.db.updateDirect({ read: false }, docPath);
  }

  public async deleteNotification(userId: string, notificationId: string): Promise<void> {
    const docPath = Users() + '/' + userId + '/' + Sub_Notifications() + '/' + notificationId;
    await this.db.delete(docPath, false);
  }

  //Generates a hash code from a given string
  private generateHashCode(value: string) {
    var hash = 0,
      i,
      chr;
    if (value.length === 0) return hash;
    for (i = 0; i < value.length; i++) {
      chr = value.charCodeAt(i);
      hash = (hash << 5) - hash + chr;
      hash |= 0; // Convert to 32bit integer
    }
    return hash;
  }

  public async getUserSpecificRights(id: string, hideSpinner: boolean = false): Promise<MethodReturn> {
    var path = Users() + '/' + id + '/' + Sub_UserRights();
    return await this.get(path, false, hideSpinner, false);
  }

  public async getUser(id: string, hideSpinner: boolean = false, clearSpinnerAtEnd: boolean = true): Promise<MethodReturn> {
    return await this.getDoc(Users(), id, false, hideSpinner, 1000, clearSpinnerAtEnd);
  }

  public async getGroups(useDocumentUidAsId: boolean = true, hideSpinner: boolean = false, clearSpinnerAtEnd: boolean = true): Promise<MethodReturn> {
    return await this.get(Groups(), false, hideSpinner, useDocumentUidAsId, clearSpinnerAtEnd);
  }

  public async getGroupRights(groupId: number, hideSpinner: boolean = false): Promise<MethodReturn> {
    const groupLookup = await this.db.get(
      Groups(),
      true,
      Properties.ID,
      groupId,
      false,
      hideSpinner,
      false,
      false
    );

    if (!groupLookup.result) {
      return groupLookup;
    }

    const groups = groupLookup.data ?? [];
    if (!groups.length) {
      return {
        result: false,
        errorMessage: `Gruppe mit id ${groupId} wurde nicht gefunden.`,
      };
    }

    const groupPath = groups[0][Properties.Path];
    if (!groupPath) {
      return {
        result: false,
        errorMessage: `Pfad der Gruppe mit id ${groupId} konnte nicht ermittelt werden.`,
      };
    }

    return await this.get(
      groupPath + '/' + Sub_UserRights(),
      false,
      hideSpinner,
      false
    );
  }


  // --------------- Calendars ------------------------------


  /**
   * Adds a new event to the database.
   * @param calendarDocPath - The path of the calendar document.
   * @param eventId - The ID of the event.
   * @param data - The data to be added to the event.
   * @returns A promise that resolves with the method return.
   */
  public async addCalendarEvent(calendarDocPath: string, data: CalendarEvent): Promise<MethodReturn> {
    return await this.db.add(data, calendarDocPath + '/' + Event());
  }

  /**
   * Serializes calendar event input from UI values into a storable calendar event object.
   * @param input - The UI payload containing event values.
   * @returns The serialized calendar event.
   */
  public serializeCalendarEventFromUi(input: {
    name: string;
    start: Date;
    end: Date;
    type: string;
    location?: string;
  }): CalendarEvent {
    const event: CalendarEvent = {
      name: input.name,
      start: firebase.firestore.Timestamp.fromDate(new Date(input.start)),
      end: firebase.firestore.Timestamp.fromDate(new Date(input.end)),
      type: input.type,
      created: firebase.firestore.Timestamp.fromDate(new Date()),
    };
    if (input.location !== undefined && input.location !== null) {
      event.location = input.location;
    }
    return event;
  }

  /**
   * Deserializes raw calendar data from the database into the shared calendar model.
   * @param raw - The raw database object.
   * @returns The mapped calendar object.
   */
  public deserializeCalendar(raw: any): Calendar {
    return {
      name: raw[Properties.Name],
      right_id: Number(raw[Properties.CalendarRightID] ?? raw.right_id ?? 0),
      created: raw[Properties.Created],
      id: raw[Properties.ID],
      path: raw[Properties.Path],
      parentPath: raw[Properties.ParentPath],
    };
  }

  /**
   * Deserializes raw event data from the database into the shared calendar event model.
   * @param raw - The raw database object.
   * @param calendar - Optional parent calendar used as fallback context.
   * @returns The mapped calendar event object.
   */
  public deserializeCalendarEvent(raw: any, calendar?: Calendar): CalendarEvent {
    return {
      name: raw[Properties.Name],
      start: raw[Properties.Start],
      end: raw[Properties.End],
      type: raw[Properties.Type] ?? calendar?.id,
      created: raw[Properties.Created] ?? firebase.firestore.Timestamp.fromDate(new Date()),
      location: raw.location ?? undefined,
      id: raw[Properties.ID],
      path: raw[Properties.Path],
      parentPath: raw[Properties.ParentPath],
    };
  }

  /**
   * Deserializes raw participant data from the database into the shared participant model.
   * @param raw - The raw database object.
   * @returns The mapped event participant object.
   */
  public deserializeCalendarEventParticipant(raw: any): CalendarEventParticipant {
    const firstName = raw.firstName ?? raw.prticipantFirstName ?? raw[Properties.Name] ?? '';
    const lastName = raw.lastName ?? raw.participantLastName ?? raw[Properties.LastName] ?? '';
    return {
      id: raw[Properties.ID],
      firstName,
      lastName,
      prticipantFirstName: raw.prticipantFirstName ?? firstName,
      participantLastName: raw.participantLastName ?? lastName,
      created: raw[Properties.Created] ?? firebase.firestore.Timestamp.fromDate(new Date()),
      path: raw[Properties.Path],
      parentPath: raw[Properties.ParentPath],
      userId: raw[Properties.UserId] ?? raw.userId ?? '',
      attending: raw.attending !== undefined ? raw.attending : true,
      excuseText: raw.excuseText ?? undefined,
    };
  }

  /**
   * Adds a participant to a calendar event.
   * @param eventDocPath - The document path of the calendar event.
   * @param data - The participant data to store.
   * @returns A promise resolving with the operation result.
   */
  public async addCalendarEventParticipant(
    eventDocPath: string,
    data: CalendarEventParticipant
  ): Promise<MethodReturn> {
    const collectionPath = eventDocPath + '/' + Sub_Participant();
    return await this.db.add(data, collectionPath);
  }

  /**
   * Updates an existing calendar event participant document.
   * @param participantDocPath - The full document path of the participant.
   * @param data - The fields to update.
   * @returns A promise resolving with the operation result.
   */
  public async updateCalendarEventParticipant(
    participantDocPath: string,
    data: any
  ): Promise<MethodReturn> {
    return await this.db.updateDirect(data, participantDocPath);
  }

  /**
   * Queries the participant subcollection of a calendar event for a specific user.
   * @param eventDocPath - The document path of the calendar event.
   * @param userId - The Firebase auth user ID to search for.
   * @returns A promise resolving with matching participant documents.
   */
  public async getCalendarEventParticipantByUserId(
    eventDocPath: string,
    userId: string
  ): Promise<MethodReturn> {
    const collectionPath = eventDocPath + '/' + Sub_Participant();
    return await this.db.get(collectionPath, true, 'userId', userId, false, true);
  }

  /**
   * Loads all participants of a calendar event.
   * @param eventDocPath - The document path of the calendar event.
   * @returns A promise resolving with all participant documents.
   */
  public async getAllCalendarEventParticipants(
    eventDocPath: string
  ): Promise<MethodReturn> {
    const collectionPath = eventDocPath + '/' + Sub_Participant();
    return await this.db.get(collectionPath, false, '', '', false, true);
  }

  /**
   * Deletes a calendar event participant by document path.
   * @param participantDocPath - The full path of the participant document.
   * @returns A promise resolving with the operation result.
   */
  public async deleteCalendarEventParticipant(
    participantDocPath: string
  ): Promise<MethodReturn> {
    return await this.db.delete(participantDocPath, true);
  }

  // ---- User rights management ----

  /**
   * Adds a right to a user's rights subcollection.
   * @param userId - The user's document ID.
   * @param data - The right data to store (id, name, state).
   * @returns A promise resolving with the operation result.
   */
  public async addUserRight(userId: string, data: Right): Promise<MethodReturn> {
    const collectionPath = Users() + '/' + userId + '/' + Sub_UserRights();
    return await this.db.add(data, collectionPath);
  }

  /**
   * Updates an existing right document for a user.
   * @param rightDocPath - The full document path of the right.
   * @param data - The fields to update.
   * @returns A promise resolving with the operation result.
   */
  public async updateUserRight(rightDocPath: string, data: any): Promise<MethodReturn> {
    return await this.db.updateDirect(data, rightDocPath);
  }

  /**
   * Deletes a right document from a user's rights subcollection.
   * @param rightDocPath - The full document path of the right.
   * @returns A promise resolving with the operation result.
   */
  public async deleteUserRight(rightDocPath: string): Promise<MethodReturn> {
    return await this.db.delete(rightDocPath, true);
  }

  /**
   * Observes all calendars and their nested events as a live stream from Firestore.
   * @returns An observable of calendar containers including mapped events.
   */
  public observeCalendarsWithEvents(): Observable<CalendarContainer[]> {
    const calendars$ = this.db.getAngularFirestore
      .collection(Calendars())
      .snapshotChanges()
      .pipe(map((actions) => this.mapCalendarActions(actions)));

    return this.observeCalendarContainers(calendars$);
  }

  /**
   * Observes only calendars whose right_id is included in the provided list and streams nested events.
   * @param allowedCalendarRightIds - list of allowed calendar right ids.
   * @returns An observable of calendar containers including mapped events.
   */
  public observeCalendarsWithEventsByRightIds(
    allowedCalendarRightIds: number[]
  ): Observable<CalendarContainer[]> {
    const normalizedRightIds = Array.from(
      new Set(
        (allowedCalendarRightIds || [])
          .map((value) => Number(value))
          .filter((value) => Number.isFinite(value) && value > 0)
      )
    );

    if (!normalizedRightIds.length) {
      return of([] as CalendarContainer[]);
    }

    const calendarsByNumberIds$ = this.createCalendarsStreamByRightIdChunks(
      normalizedRightIds
    );

    const calendars$ = calendarsByNumberIds$.pipe(
      mergeMap((numberMatchedCalendars) => {
        if (numberMatchedCalendars.length > 0) {
          return of(numberMatchedCalendars);
        }

        const stringRightIds = normalizedRightIds.map((value) => value.toString());
        return this.createCalendarsStreamByRightIdChunks(stringRightIds);
      })
    );

    return this.observeCalendarContainers(calendars$);
  }

  /**
   * Builds a merged calendar stream using chunked in-queries for right_id values.
   */
  private createCalendarsStreamByRightIdChunks(
    rightIds: Array<number | string>
  ): Observable<Calendar[]> {
    const normalizedRightIds = Array.from(new Set(rightIds));
    if (!normalizedRightIds.length) {
      return of([] as Calendar[]);
    }

    const chunkSize = 10;
    const rightIdChunks: Array<Array<number | string>> = [];
    for (let index = 0; index < normalizedRightIds.length; index += chunkSize) {
      rightIdChunks.push(normalizedRightIds.slice(index, index + chunkSize));
    }

    const calendarStreams = rightIdChunks.map((rightIdChunk) =>
      this.db.getAngularFirestore
        .collection(Calendars(), (ref) =>
          ref.where(Properties.CalendarRightID, 'in', rightIdChunk as any[])
        )
        .snapshotChanges()
        .pipe(map((actions) => this.mapCalendarActions(actions)))
    );

    return combineLatest(calendarStreams).pipe(
      map((calendarGroups) => {
        const merged = calendarGroups.reduce(
          (acc, current) => acc.concat(current),
          [] as Calendar[]
        );
        const byId = new Map<string, Calendar>();
        merged.forEach((calendar) => {
          if (calendar?.id) {
            byId.set(calendar.id, calendar);
          }
        });
        return Array.from(byId.values());
      })
    );
  }

  /**
   * Maps Firestore snapshot actions to calendar models.
   */
  private mapCalendarActions(actions: any[]): Calendar[] {
    return actions.map((action) => {
      const data = action.payload.doc.data() as any;
      return this.deserializeCalendar({
        ...data,
        [Properties.ID]: action.payload.doc.id,
        [Properties.Path]: action.payload.doc.ref.path,
        [Properties.ParentPath]: action.payload.doc.ref.parent.path,
      });
    });
  }

  /**
   * Builds calendar containers by combining calendar streams with nested event streams.
   */
  private observeCalendarContainers(
    calendars$: Observable<Calendar[]>
  ): Observable<CalendarContainer[]> {
    return calendars$.pipe(
      mergeMap((calendars) => {
        if (!calendars.length) {
          return of([] as CalendarContainer[]);
        }

        const observables = calendars.map((calendar) =>
          this.db.getAngularFirestore
            .collection(calendar.path + '/' + Event())
            .snapshotChanges()
            .pipe(
              map((actions) => {
                const events = actions.map((action) => {
                  const data = action.payload.doc.data() as any;
                  return this.deserializeCalendarEvent(
                    {
                      ...data,
                      [Properties.ID]: action.payload.doc.id,
                      [Properties.Path]: action.payload.doc.ref.path,
                      [Properties.ParentPath]: action.payload.doc.ref.parent.path,
                    },
                    calendar
                  );
                });

                return {
                  calendar,
                  events,
                } as CalendarContainer;
              })
            )
        );

        return combineLatest(observables);
      })
    );
  }

  /**
   * Updates an existing event in the database.
   * @param eventDocPath - The path of the event document.
   * @param data - The data to update the event with.
   * @returns A promise that resolves with the method return.
   */
  public async updateCalendarEvent(eventDocPath: string, data: CalendarEvent): Promise<MethodReturn> {
    //check if existing
    var res = await this.getByPath(eventDocPath);
    if (!res.result) {
      return { result: false, errorMessage: 'Kalender existiert nicht' };
    }
    return await this.db.updateDirect(data, eventDocPath);
  }

  /**
   * Deletes an event from the database.
   * @param eventPath - The path of the event document.
   * @returns A promise that resolves with the method return.
   */
  public async deleteCalendarEvent(eventDocPath: string): Promise<MethodReturn> {
    //check if existing
    var res = await this.getByPath(eventDocPath);
    if (!res.result) {
      return { result: false, errorMessage: 'Kalender existiert nicht' };
    }
    return await this.db.delete(eventDocPath, true);
  }

  // --------------------------------------------------------


  //-------------------------Testing--------------------------------------
  groups: Group[] = [];
  users: User[] = [];

  public setTestGroups(groups: Group[]) {
    //intentionally do nothing here
  }
  public setTestUsers(users: User[]) {
    //intentionally do nothing here
  }
}


